import { types, resolveIdentifier, getRoot, flow } from 'mobx-state-tree';
import { MediaFileSelectState } from './MediaFileSelectState';
import MediaFileStore from '../../../stores/domain/MediaFileStore';
import { MediaFile } from '../../../models/mediaFiles/MediaFileModel';

/**
 * MST-based manager for creating and managing MediaFileSelectState instances
 * based on uiSchema field definitions
 */
export const MediaFileSelectStateManager = types.model('MediaFileSelectStateManager', {
  managerUuid: types.identifier,
  // Store dynamic MediaFileSelectState instances by field name
  states: types.map(MediaFileSelectState)
}).actions(self => ({
  /**
   * Parse uiSchema and find all fields that use mediaFileCodeUploadWidget variants
   * @param {Object} uiSchema - The UI schema object
   * @returns {Array} Array of field names that use media file widgets
   */
  findMediaFileFields(uiSchema) {
    console.log('MediaFileSelectStateManager.findMediaFileFields searching uiSchema:', uiSchema);
    const mediaFileFields = [];
    const mediaFileWidgets = [
      'mediaFileCodeUploadWidget',
      // Add more widget variants as needed
    ];

    function searchSchema(obj, path = []) {
      if (obj && typeof obj === "object") {
        for (const [key, value] of Object.entries(obj)) {
          if (key === "ui:widget" && mediaFileWidgets.includes(value)) {
            // Found a media file widget, record the field path
            const fieldName = path.length > 0 ? path[path.length - 1] : 'root';
            console.log(`MediaFileSelectStateManager.findMediaFileFields found field: ${fieldName}, widget: ${value}, path: ${path.join('.')}`);
            mediaFileFields.push({
              fieldName,
              widgetType: value,
              fieldPath: path.join('.')
            });
          } else if (typeof value === "object") {
            // Continue searching deeper, building the path
            searchSchema(value, [...path, key]);
          }
        }
      }
    }

    searchSchema(uiSchema);
    console.log(`MediaFileSelectStateManager.findMediaFileFields found ${mediaFileFields.length} fields:`, mediaFileFields.map(f => f.fieldName));
    return mediaFileFields;
  },

  /**
   * Initialize MediaFileSelectState instances for all media file fields in a schema
   * @param {Object} uiSchema - The UI schema object
   * @param {Function} consumerCallback - Optional callback to set on each state
   */
  initializeFromUISchema(uiSchema, consumerCallback = null) {
    // Clear existing states
    self.states.clear();

    const mediaFileFields = self.findMediaFileFields(uiSchema);
    const root = getRoot(self);

    mediaFileFields.forEach(({ fieldName, widgetType }) => {
      // Use the factory method for consistent creation
      const stateInstance = self.createMediaFileSelectState(fieldName, consumerCallback);

      if (stateInstance) {
        // Store in the map
        self.states.set(fieldName, stateInstance);
      }
    });
  },

  /**
   * Create a single MediaFileSelectState instance
   * @param {string} fieldName - The field identifier
   * @param {Function} consumerCallback - Optional callback for handling selections
   * @param {boolean} isMediaFileEnabled - Whether media file functionality is enabled
   * @returns {Object|null} Created MediaFileSelectState instance or null if error
   */
  createMediaFileSelectState(fieldName, consumerCallback = null, isMediaFileEnabled = false) {
    try {
      const root = getRoot(self);

      // Create MediaFileSelectState with proper MST context
      const stateInstance = MediaFileSelectState.create({
        selectStateUuid: fieldName,
        store: resolveIdentifier(MediaFileStore, root, 'mfs'),
        isMediaFileEnabled: isMediaFileEnabled
      });

      // Set up consumer callback if provided
      if (consumerCallback && typeof consumerCallback === 'function') {
        stateInstance.setConsumer(consumerCallback);
      }

      return stateInstance;
    } catch (error) {
      console.error(`Failed to create MediaFileSelectState for field ${fieldName}:`, error);
      return null;
    }
  },

  /**
   * Get MediaFileSelectState for a specific field
   * @param {string} fieldName - The field name
   * @returns {Object|null} The MediaFileSelectState instance or null
   */
  getStateForField(fieldName) {
    return self.states.get(fieldName) || null;
  },

  /**
   * Check if a state exists for a specific field
   * @param {string} fieldName - The field name
   * @returns {boolean} True if state exists
   */
  hasStateForField(fieldName) {
    return self.states.has(fieldName);
  },

  /**
   * Remove state for a specific field
   * @param {string} fieldName - The field name
   */
  removeStateForField(fieldName) {
    if (self.states.has(fieldName)) {
      self.states.delete(fieldName);
    }
  },

  /**
   * Clear all states
   */
  clearAllStates() {
    self.states.clear();
  },

  /**
   * Hydrate media file states from existing code data
   * @param {Object} codeData - The existing code data with mediaFiles
   * @param {Object} mediaFileManager - The media file manager for API calls
   * @returns {Object} Summary info about the hydration for the calling code
   */
  hydrateFromCodeData: flow(function* (codeData, mediaFileManager) {
    if (!codeData?.mediaFiles) return { hydratedCount: 0, firstMediaFile: null };

    let hydratedCount = 0;
    let firstMediaFile = null;
    let firstStateInstance = null;

    for (const [fieldName, mediaFileUuid] of Object.entries(codeData.mediaFiles)) {
      const stateInstance = self.getStateForField(fieldName);

      if (stateInstance && mediaFileUuid) {
        try {
          // Get the full media file data from the API
          const mediaFile = yield mediaFileManager.getMediaFile(mediaFileUuid);

          if (mediaFile?.fileLocation?.singleUseFile) {
            // For single-use files, add to the store list first then select
            stateInstance.store.addFileToList(mediaFile.fileLocation);
            stateInstance.select(mediaFile.fileLocation);
          } else {
            // For regular files, select by UUID with field name
            stateInstance.select({ uuid: mediaFileUuid }, fieldName);
          }

          // Track first media file info for backward compatibility
          if (hydratedCount === 0) {
            firstMediaFile = mediaFile;
            firstStateInstance = stateInstance;
          }

          hydratedCount++;
        } catch (error) {
          console.error(`Failed to hydrate media file for field ${fieldName}:`, error);
        }
      } else if (!stateInstance) {
        console.warn(`No state instance found for field: ${fieldName}`);
      }
    }

    // Return info that can be used to create a MediaFile for backward compatibility
    return {
      hydratedCount,
      firstMediaFile,
      firstStateInstance,
      // Create a generic MediaFile instance using info from the first hydrated state
      createGenericMediaFile: () => {
        if (firstStateInstance?.selected) {
          return MediaFile.create({
            uuid: "mediaFileEdit",
            singleUseFile: firstStateInstance.selected.singleUseFile || false
          });
        }
        return null;
      }
    };
  })
})).views(self => ({
  /**
   * Get all field names that have states
   * @returns {Array} Array of field names
   */
  get fieldNames() {
    return Array.from(self.states.keys());
  },

  /**
   * Get count of managed states
   * @returns {number} Number of managed states
   */
  get stateCount() {
    return self.states.size;
  }
}));

export default MediaFileSelectStateManager;
